import React, { useEffect, useState, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { post } from 'aws-amplify/api';
import { getCurrentUser, fetchAuthSession } from 'aws-amplify/auth';

// ---- NEW: quota flag + helper ----
const PCC_QUOTA_KEY = 'pcc_quota_exceeded';

function markPccQuotaExceeded() {
  try {
    sessionStorage.setItem(PCC_QUOTA_KEY, '1');
  } catch (e) {
    console.warn('Could not persist PCC quota flag', e);
  }
}

// ---- NEW: simple 20ms gap between PCC calls from this module ----
const MIN_PCC_GAP_MS = 20;
let lastPccCallTime = 0;

async function ensureMinPccGap() {
  const now = Date.now();
  const elapsed = now - lastPccCallTime;
  const wait = Math.max(0, MIN_PCC_GAP_MS - elapsed);
  if (wait > 0) {
    await new Promise((resolve) => setTimeout(resolve, wait));
  }
  lastPccCallTime = Date.now();
}

/**
 * PCCCallbackHandler
 *
 * Purpose:
 *  - Runs when PCC redirects back to your app with an OAuth "code".
 *  - Exchanges the code for PCC tokens via your /exchangePCCCode Lambda.
 *  - Stores PCC session + UI options in sessionStorage for later use.
 *  - (New) Captures a summary of the background patient sync for UX/telemetry.
 *
 * Key ideas:
 *  - We authenticate the REST call with Cognito User Pool (JWT), not with PCC tokens.
 *  - We keep PCC tokens in sessionStorage to call PCC (server-side) later.
 *  - We don’t force-select any facility or note type; UI reads options and lets the user pick.
 */
const PCCCallbackHandler = () => {
  const [status, setStatus] = useState('Exchanging code...');
  const navigate = useNavigate();

  // Prevent running twice (React StrictMode renders effects twice in dev)
  const didRun = useRef(false);

  useEffect(() => {
    if (didRun.current) {
      console.warn("⚠️ PCCCallbackHandler already ran. Skipping duplicate call.");
      return;
    }
    didRun.current = true;

    // --- 1) Read OAuth redirect params and validate anti-CSRF state ---
    const code = new URLSearchParams(window.location.search).get('code');
    const returnedState = new URLSearchParams(window.location.search).get('state');
    const originalState = sessionStorage.getItem('pcc_auth_state');

    console.log("📍 Redirect URL:", window.location.href);
    console.log("🔁 Params:", { code, returnedState, originalState });

    if (!code || !returnedState || returnedState !== originalState) {
      console.warn("⚠️ Invalid or missing code/state. Aborting.");
      setStatus("❌ Invalid or tampered redirect.");
      return;
    }

    // --- 2) Exchange the authorization code for PCC tokens via your REST API ---
    const exchangeCodeForTokens = async () => {
      try {
        // (a) Ensure the app user is signed in so we can call API Gateway with User Pool auth
        try {
          const user = await getCurrentUser();
          console.log("✅ Signed in as:", user);
        } catch {
          console.warn("❌ Not signed in.");
          setStatus("❌ Must be signed in to complete PointClickCare linking.");
          return;
        }

        // (b) Get Cognito JWT for the REST call (Amplify can attach it if you pass authMode: 'userPool')
        const session = await fetchAuthSession();
        const idToken = session.tokens?.idToken?.toString();

        if (!idToken) {
          console.warn("❌ No ID token found in session.");
          setStatus("❌ Missing ID token for secure API call.");
          return;
        }

        // (c) Prepare payload with the redirectUri your PCC app is configured to use
        const redirectUri = process.env.REACT_APP_PCC_REDIRECT_URI;
        const payload = { code, redirectUri };

        // (d) Call your REST API -> Lambda does the actual token exchange with PCC
        //     We use Amazon Cognito User Pool auth for API Gateway (NOT PCC token).

        // 🔹 NEW: enforce 20ms min gap before this PCC call
        await ensureMinPccGap();

        const result = await post({
          apiName: 'pccapi',
          path: '/exchangePCCCode',
          options: {
            body: payload,
            headers: {
              // You *can* omit this; authMode: 'userPool' signs the request automatically.
              Authorization: idToken,
              'Content-Type': 'application/json',
            },
            authMode: 'userPool',
          },
        }).response;

        // (e) Parse Lambda response safely
        let data;
        try {
          const text = await result.body.text();
          data = JSON.parse(text);
        } catch (err) {
          console.warn("❌ Failed to parse body as JSON:", err);
          setStatus("❌ Could not read response.");
          return;
        }

        // 🔹 NEW: handle quota exceeded (429) from your Lambda / usage plan
        if (result.statusCode === 429) {
          console.warn("🚫 PCC quota reached (429):", data);
          markPccQuotaExceeded();
          setStatus("❌ PCC quota reached, try again later.");
          // Do NOT proceed to store tokens; just stop here.
          return;
        }

        // (f) Validate success contract
        if (result.statusCode !== 200) {
          throw new Error(data?.error || `Exchange failed with statusCode ${result.statusCode}`);
        }
        if (!data.access_token || !data.orgUuid || !data.pccUserId) {
          setStatus("❌ Incomplete response from PointClickCare. Cannot link account.");
          return;
        }

        // --- 3) Cache PCC session + UI options in sessionStorage ---
        // These are consumed by your hooks/UI (e.g., usePCCSession and selects)
        sessionStorage.setItem('pcc_accessToken', data.access_token);
        sessionStorage.setItem('pcc_refreshToken', data.refresh_token);
        sessionStorage.setItem('pcc_tokenExpiresAt', Date.now() + parseInt(data.expires_in) * 1000);
        sessionStorage.setItem('pcc_orgUuid', data.orgUuid);
        sessionStorage.setItem('pcc_userId', data.pccUserId);

        if (data.metadata) {
          sessionStorage.setItem('pcc_orgId', data.metadata.orgId || '');
          sessionStorage.setItem('pcc_appEndUser', data.metadata.appEndUser || '');
        }

        // UI-friendly options (ids-as-strings) for facility + note type selects
        if (data.facilitiesOptions) {
          sessionStorage.setItem('pcc_facilitiesOptions', JSON.stringify(data.facilitiesOptions));
        }
        if (data.noteTypeOptions) {
          sessionStorage.setItem('pcc_noteTypeOptions', JSON.stringify(data.noteTypeOptions));
        }

        // Soft default for facility; do NOT auto-select in UI
        if (data.facId) {
          sessionStorage.setItem('pcc_defaultFacId', String(data.facId));
        }

        // Back-compat: keep raw arrays if the UI still reads them anywhere
        if (data.facilities) {
          sessionStorage.setItem('pcc_facilities', JSON.stringify(data.facilities));
        }
        if (data.noteTypes) {
          sessionStorage.setItem('pcc_noteTypes', JSON.stringify(data.noteTypes));
        }

        // --- 4) Record patient sync summary + "last synced" timestamp (if present) ---
        if (data.patientsSync) {
          try {
            sessionStorage.setItem('pcc_patientsSync', JSON.stringify(data.patientsSync));
            if (data.patientsSync.finished) {
              sessionStorage.setItem('pcc_patientsSyncedAt', new Date().toISOString());
            }
          } catch (e) {
            console.warn("⚠️ Could not store patientsSync:", e);
          }
        }

        // --- 5) Done → navigate home
        setStatus('✅ PointClickCare login successful. Redirecting...');
        setTimeout(() => navigate('/'), 1500);

      } catch (err) {
        console.error("🔥 PointClickCare Token Exchange Error:", err);

        // 🔹 NEW: backup quota detection if Amplify throws on 429
        const status =
          err?.response?.status ||
          err?.$metadata?.httpStatusCode ||
          err?.statusCode ||
          err?.status;

        if (status === 429 || /quota/i.test(err?.message || '')) {
          console.warn("🚫 PCC quota reached (caught in catch):", err);
          markPccQuotaExceeded();
          setStatus("❌ PCC quota reached, try again later.");
          return;
        }

        setStatus('❌ Failed to link PointClickCare account.');
        setTimeout(() => navigate('/'), 3000);
      }
    };

    exchangeCodeForTokens();
  }, [navigate]);

  return (
    <div className="p-6 text-xl">
      <p>{status}</p>
    </div>
  );
};

export default PCCCallbackHandler;
